import React, { useState } from 'react';
import { $Form, $Helptext } from 'styles';
import { FormGroup, TextField } from 'components';
import { useDispatch } from 'react-redux';
import { useTypedSelector } from 'types/global';
import { postCreateCustomerRequest, selectHyperlink } from 'modules/hyperlink';
import { CustomerAccountType } from 'modules/hyperlink/hyperlink.types';
import { AvsIdentificationType } from 'modules/avs';
import { getLanguageCode, i18n } from 'i18n';

import HyperlinkContainer from './HyperlinkContainer';
import {
  $Button,
  $ErrorText,
  $FormWrapper,
  $Image,
  $ImageWrapper,
  $InputsWrapper,
  $LabelWrapper,
  $FormLabel,
} from './AddCustomerIdentifier.styled';

const NATIONAL_ID_REGEX = /^[12][0-9]{9}$/;

function AddCustomerIdentifier() {
  const [nationalId, setNationalId] = useState('');
  const [error, setError] = useState('');
  const dispatch = useDispatch();
  const { data, creatingCustomer, createCustomerError } =
    useTypedSelector(selectHyperlink);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!NATIONAL_ID_REGEX.test(nationalId)) {
      setError(
        i18n('hyperlink.add_customer_identifier.invalid_national_id_msg'),
      );
      return;
    }
    dispatch(
      postCreateCustomerRequest({
        sandbox: data?.application?.sandbox,
        applicationId: data?.application?.application_id,
        accountType:
          data?.application?.account_type ?? CustomerAccountType.Personal,
        identifiers: [
          {
            type: AvsIdentificationType.NATIONAL_ID,
            value: nationalId,
          },
        ],
      }),
    );
    setError('');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setNationalId(value);
    if (value === '') {
      setError('');
    }
  };

  return (
    <HyperlinkContainer>
      <$ImageWrapper>
        <$Image src="/icons/id-card.svg" />
      </$ImageWrapper>
      <$FormWrapper>
        <$Form onSubmit={handleSubmit}>
          <FormGroup>
            <$LabelWrapper lang={getLanguageCode()}>
              <$FormLabel>
                {i18n('hyperlink.add_customer_identifier.form_label_header')}
              </$FormLabel>
              {getLanguageCode() === 'en' && (
                <>
                  <br />
                  <$FormLabel>
                    {i18n(
                      'hyperlink.add_customer_identifier.form_label_footer',
                    )}
                  </$FormLabel>
                </>
              )}
            </$LabelWrapper>
          </FormGroup>
          <FormGroup>
            <$InputsWrapper>
              <TextField
                type="text"
                value={nationalId}
                helptext={i18n('hyperlink.add_customer_identifier.helptext')}
                name="name"
                data-cy="national_id"
                onChange={handleChange}
                data-test-id="national_id"
                placeholder={i18n(
                  'hyperlink.add_customer_identifier.placeholder',
                )}
              />
              {createCustomerError && (
                <$ErrorText>{createCustomerError}</$ErrorText>
              )}
              {error && <$ErrorText>{error}</$ErrorText>}
            </$InputsWrapper>
          </FormGroup>
          <$Button
            isLoading={creatingCustomer}
            type="submit"
            styledProps={{
              align: 'center',
              justifyContent: 'center',
              language: getLanguageCode(),
            }}
          >
            {i18n('hyperlink.add_customer_identifier.continue_btn')}
          </$Button>
        </$Form>
        <$Helptext>
          {i18n('hyperlink.add_customer_identifier.form_footer')}
        </$Helptext>
      </$FormWrapper>
    </HyperlinkContainer>
  );
}

export default AddCustomerIdentifier;
